import Component from "@glimmer/component";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import PluginOutlet from "discourse/components/plugin-outlet";
import ActionList from "discourse/components/topic-list/action-list";
import NewRepliesDot from "discourse/components/topic-list/new-replies-dot";
import ParticipantGroups from "discourse/components/topic-list/participant-groups";
import TopicExcerpt from "discourse/components/topic-list/topic-excerpt";
import TopicLink from "discourse/components/topic-list/topic-link";
import UnreadIndicator from "discourse/components/topic-list/unread-indicator";
import TopicPostBadges from "discourse/components/topic-post-badges";
import TopicStatus from "discourse/components/topic-status";
import dAvatar from "discourse/ui-kit/helpers/d-avatar";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import lazyHash from "discourse/helpers/lazy-hash";
import dNumber from "discourse/ui-kit/helpers/d-number";
import topicFeaturedLink from "discourse/helpers/topic-featured-link";
import { groupPath } from "discourse/lib/url";
import dCategoryLink from "discourse/ui-kit/helpers/d-category-link";
import dDiscourseTags from "discourse/ui-kit/helpers/d-discourse-tags";

export default class TopicCell extends Component {
  get participantGroups() {
    if (!this.args.topic.get("participant_groups")) {
      return [];
    }

    return this.args.topic.get("participant_groups").map((name) => ({
      name,
      url: groupPath(name),
    }));
  }

  extractPrice(title) {
    const matches = title?.match(/([0-9]{1,3}(?:.[0-9]{1,2})?) USD/);
    
    return matches == null ? "" : "$" + matches[1];
  }

  @action
  onTitleFocus(event) {
    event.target.closest(".topic-list-item").classList.add("selected");
  }

  @action
  onTitleBlur(event) {
    event.target.closest(".topic-list-item").classList.remove("selected");
  }

  <template>
    <td class="main-link topic-list-data" colspan="1">
      <PluginOutlet
        @name="topic-list-before-link"
        @outletArgs={{lazyHash topic=@topic}}
      />

      <span class="link-top-line" role="heading" aria-level="2">
        {{~! no whitespace ~}}
        <PluginOutlet
          @name="topic-list-before-status"
          @outletArgs={{lazyHash topic=@topic}}
        />
        {{~! no whitespace ~}}
        <PluginOutlet
          @name="topic-list-topic-cell-link-top-line"
          @outletArgs={{lazyHash topic=@topic tagsForUser=@tagsForUser}}
        >
          {{~! no whitespace ~}}
          <TopicStatus @topic={{@topic}} @context="topic-list" />
          {{~! no whitespace ~}}
          <TopicLink
            {{on "focus" this.onTitleFocus}}
            {{on "blur" this.onTitleBlur}}
            @topic={{@topic}}
            class="raw-link raw-topic-link"
          />
          {{~#if @topic.featured_link~}}
            &nbsp;
            {{~topicFeaturedLink @topic}}
          {{~/if~}}
          <PluginOutlet
            @name="topic-list-after-title"
            @outletArgs={{lazyHash topic=@topic}}
          />
          {{~! no whitespace ~}}
          <UnreadIndicator @topic={{@topic}} />
          {{~#if @topic.is_nested_view~}}
            {{~#if @topic.has_new_replies~}}
              <NewRepliesDot @topic={{@topic}} />
            {{~/if~}}
          {{~else if @showTopicPostBadges~}}
            <TopicPostBadges
              @unreadPosts={{@topic.unread_posts}}
              @unseen={{@topic.unseen}}
              @url={{@topic.lastUnreadUrl}}
            />
          {{~/if~}}
          <PluginOutlet
            @name="topic-list-after-badges"
            @outletArgs={{lazyHash topic=@topic}}
          />
        </PluginOutlet>
      </span>
        
      <div class="image">
        {{#if @topic.image_url}}
          <div style="background-image: url({{ @topic.image_url }})"></div>
        {{/if}}
      </div>

      <div class="releases-metadata">
        {{dIcon "heart"}} {{dNumber @topic.like_count}} {{dIcon "far-eye"}} {{dNumber @topic.views}} {{dIcon "reply"}} {{dNumber @topic.replyCount}}
        {{dDiscourseTags @topic mode="list" tagsForUser=@tagsForUser}}
      </div>

      <div class="releases-cost">{{~this.extractPrice @topic.excerpt}}</div>

      <div class="releases-protection">
        {{dIcon "shield-halved"}} Escrow
      </div>

      {{dAvatar @topic.posters.[0] avatarTemplatePath="user.avatar_template" usernamePath="user.username" namePath="user.name" imageSize="large"}}

      <div class="link-bottom-line">
        <PluginOutlet
          @name="topic-list-topic-cell-link-bottom-line"
          @outletArgs={{lazyHash topic=@topic tagsForUser=@tagsForUser}}
        >
          <PluginOutlet
            @name="topic-list-before-category"
            @outletArgs={{lazyHash topic=@topic}}
          />
          {{#unless @hideCategory}}
            {{#unless @topic.isPinnedUncategorized}}
              {{dCategoryLink @topic.category}}
            {{/unless}}
          {{/unless}}
          <PluginOutlet
            @name="topic-list-after-category"
            @outletArgs={{lazyHash topic=@topic}}
          />

          {{#if this.participantGroups}}
            <ParticipantGroups @groups={{this.participantGroups}} />
          {{/if}}

          <ActionList
            @topic={{@topic}}
            @postNumbers={{@topic.liked_post_numbers}}
            @icon="heart"
            class="likes"
          />
        </PluginOutlet>
      </div>

      {{#if @expandPinned}}
        <TopicExcerpt @topic={{@topic}} />
      {{/if}}

      <PluginOutlet
        @name="topic-list-main-link-bottom"
        @outletArgs={{lazyHash topic=@topic expandPinned=@expandPinned}}
      />
    </td>
  </template>
}
